import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Search, Pill, Filter, CheckCircle, Globe } from 'lucide-react';
import { drugs, searchDrugs, EnhancedMedicine } from '../data/enhancedMedicines';

const DrugsPage: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedClass, setSelectedClass] = useState<string>('');

  const therapeuticClasses = useMemo(() => {
    const classes = new Set(drugs.map((d: EnhancedMedicine) => d.therapeuticClass));
    return Array.from(classes).sort() as string[];
  }, []);

  const filteredDrugs = useMemo(() => {
    let results = searchQuery ? searchDrugs(searchQuery) : drugs;
    if (selectedClass) {
      results = results.filter((d: EnhancedMedicine) => d.therapeuticClass === selectedClass);
    }
    return results;
  }, [searchQuery, selectedClass]);

  const getCostBadge = (affordability?: string) => {
    if (!affordability) return null;
    const lower = affordability.toLowerCase();
    if (lower.includes('very affordable') || lower.includes('low cost')) {
      return <span className="text-xs bg-green-100 text-green-800 px-2 py-0.5 rounded-full">Low Cost</span>;
    }
    if (lower.includes('affordable') || lower.includes('moderate')) {
      return <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-0.5 rounded-full">Moderate</span>;
    }
    return <span className="text-xs bg-red-100 text-red-800 px-2 py-0.5 rounded-full">High Cost</span>;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-gradient-to-r from-teal-600 to-teal-700 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="flex items-center gap-3 mb-4">
            <Pill className="w-10 h-10" />
            <h1 className="text-3xl font-bold">Drug Database</h1>
          </div>
          <p className="text-teal-100 text-lg max-w-2xl">
            Comprehensive medication information with African healthcare context, 
            including availability, affordability, and regional resistance patterns.
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-6">
        <div className="bg-white rounded-xl shadow-lg p-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search by drug name, class, or indication..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              />
            </div>
            <div className="relative">
              <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <select
                value={selectedClass}
                onChange={(e) => setSelectedClass(e.target.value)}
                className="pl-10 pr-8 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent appearance-none bg-white min-w-[200px]"
              >
                <option value="">All Classes</option>
                {therapeuticClasses.map((cls: string) => (
                  <option key={cls} value={cls}>{cls}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="mt-4 text-sm text-gray-600">
            Showing {filteredDrugs.length} of {drugs.length} medications
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredDrugs.map((drug: EnhancedMedicine) => (
            <Link
              key={drug.id}
              to={`/drugs/${drug.id}`}
              className="bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow p-6 border border-gray-100"
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Pill className="w-5 h-5 text-teal-600" />
                  <h3 className="font-semibold text-gray-800">{drug.genericName}</h3>
                </div>
                <CheckCircle className="w-5 h-5 text-green-500" />
              </div>
              <p className="text-sm text-teal-600 mb-2">{drug.therapeuticClass}</p>
              {drug.brandNames && drug.brandNames.length > 0 && (
                <p className="text-xs text-gray-500 mb-3">
                  {drug.brandNames.slice(0, 3).join(', ')}
                  {drug.brandNames.length > 3 && ` +${drug.brandNames.length - 3} more`}
                </p>
              )}
              {drug.indications && drug.indications.length > 0 && (
                <div className="mb-3">
                  <p className="text-xs text-gray-600">
                    {drug.indications.slice(0, 3).map((i) => i.condition).join(' • ')}
                    {drug.indications.length > 3 && ` +${drug.indications.length - 3} more`}
                  </p>
                </div>
              )}
              <div className="flex items-center justify-between mt-4 pt-3 border-t border-gray-100">
                {drug.africanContext && (
                  <div className="flex items-center gap-1 text-xs text-gray-500">
                    <Globe className="w-3 h-3" />
                    African Context
                  </div>
                )}
                {getCostBadge(drug.africanContext?.affordability)}
              </div>
            </Link>
          ))}
        </div>

        {filteredDrugs.length === 0 && (
          <div className="text-center py-12">
            <Pill className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-700 mb-2">No medications found</h3>
            <p className="text-gray-500">Try adjusting your search or filter criteria</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default DrugsPage;
